import * as _ from 'lodash';

import { RawVulnStatus } from '../../../typings/enums';
import type { CurrentRawVuln } from '../../../shared/model-typings/currentrawvuln';

import ModalReviewRawVuln from './ModalReviewRawVuln';

export type FieldEntry = {
  id: number;
  source: string;
  value: any; // Field value
};

export type RawVulnFormData = {
  [field in string]: FieldEntry[]
};

export type ModalErrorArray = {
  [field in string]: boolean | boolean[]
};

/**
 * Builds index used to group raw vulns
 * @param cve string
 * @param projectName string
 * @param fetcher string
 * @returns string - group index
 */
export const getGroupedIndex = (cve: string, projectName: string, fetcher: string) => `${cve}-${projectName}-${fetcher}`;

/**
 * Builds an empty errors array to use in error checking process
 * This array contains all form errors
 * @param rawVulnsToEdit RawVulnFormData
 * @returns empty ModalErrorArray
 */
export const buildEmptyErrorsArray = (rawVulnsToEdit: RawVulnFormData): ModalErrorArray => {
  const errorsArr = {};

  _.forEach(rawVulnsToEdit, (field: FieldEntry[], fieldName: string) => {
    if (!errorsArr[fieldName]) {
      errorsArr[fieldName] = [];
    }

    field.forEach((fieldEntry: FieldEntry, rowIndx: number) => {
      if (ModalReviewRawVuln.editableFields[fieldName]?.isList) { // Handle list fields
        if (!errorsArr[fieldName][rowIndx]) {
          errorsArr[fieldName][rowIndx] = [];
        }
        (fieldEntry.value || []).forEach((_item, indx: number) => {
          errorsArr[fieldName][rowIndx][indx] = true;
        });
      } else if (!errorsArr[fieldName][rowIndx]) {
        errorsArr[fieldName][rowIndx] = true;
      }
    });
  });

  return errorsArr;
};

/**
 * Populate errors array with actual validation error values
 * Determines if form inputs contains validation errors
 * @param formData RawVulnFormData - form data
 * @param errorsArr ModalErrorArray - existing errors array
 * @returns ModalErrorArray - updated errors array
 */
export const populateErrorsArray = (formData: RawVulnFormData, errorsArr: ModalErrorArray): ModalErrorArray => {
  const newErrors = _.cloneDeep(errorsArr);

  _.forEach(formData, (field: FieldEntry[], fieldName: string) => {
    const isValidFn = ModalReviewRawVuln.editableFields[fieldName]?.isValid;
    if (!isValidFn) {
      return;
    } // If no validation field - skip

    field.forEach((fieldEntry: FieldEntry, rowIndx: number) => {
      // Handle list fields
      if (ModalReviewRawVuln.editableFields[fieldName]?.isList) {
        (fieldEntry.value || []).forEach((item, indx) => {
          newErrors[fieldName][rowIndx][indx] = isValidFn(item, formData); // Validate list values
        });
        return;
      }
      // Handle normal fields
      newErrors[fieldName][rowIndx] = isValidFn(fieldEntry.value, formData);
    });
  });

  return newErrors;
};

/**
 * fieldEntryCompareFn to remove duplicate values in FieldEntry list
 *
 * @param rowA valueRow - rowA for compare
 * @param rowB valueRow - rowB for compare
 */
const fieldEntryCompareFn = (entryA: FieldEntry, entryB: FieldEntry) => _.isEqual(entryA.value, entryB.value);

/**
 * De-duplicates and compacts values of a field
 * @param field FieldEntry[] one field's field entries
 * @returns FieldEntry[]
 */
const dedupeAndCompactFieldValues = (field: FieldEntry[]) => {
  const reducedField = _.uniqWith(field, fieldEntryCompareFn).reduce(
    (fieldRes, val) => {
      if (!val.value || (_.isArray(val.value) && _.isEmpty(val.value))) {
        return fieldRes;
      }

      fieldRes.push(val);
      return fieldRes;
    },
    [],
  );

  // Return default values to any empty fields
  if (_.isEmpty(reducedField)) {
    reducedField.push(_.head(field));
  }

  return reducedField;
};

/**
 * Transforms a list of like RawVulns to RawVulnFormData
 * @param rawVulns CurrentRawVuln[] - data to add to form
 * @returns RawVulnFormData
 */
export const transformToFormData = (rawVulns: CurrentRawVuln[]): RawVulnFormData => (rawVulns || []).reduce(
  (acc, vuln: CurrentRawVuln) => {
    const id = vuln.rawVulnAuditId;
    const source = vuln.source;

    _.forEach(vuln, (value, field) => {
      (acc[field] || (acc[field] = [])).push({ id, source, value });
    });

    return _.mapValues(
      acc,
      dedupeAndCompactFieldValues, // Remove any duplicate values within a given field
    );
  },
  {},
);

/**
 * Transforms a list of RawVulnFormData to RawVulns
 * @param formData RawVulnFormData
 * @param originalVulns CurrentRawVuln[] - original prop values
 * @returns CurrentRawVuln[] - with updated values
 */
export const transformToRawVulns =
  (formData: RawVulnFormData, originalVulns: CurrentRawVuln[]): CurrentRawVuln[] =>
    originalVulns.map((rawVuln: CurrentRawVuln) => {
      _.forEach(formData, (field: FieldEntry[], fieldName: string) => {
        const matchingFieldEntry = getMatchingField(rawVuln, field, fieldName);
        if (matchingFieldEntry) {
          // eslint-disable-next-line no-param-reassign
          rawVuln[fieldName] = matchingFieldEntry.value;
        }
      });

      return rawVuln;
    });

/**
 * Get matching field functionality for transformToRawVulns.
 * Fills all status fields. FormData status field is guaranteed to be one long so we can safely take
 * the head and fill all RawVuln status fields with it.
 * @param rawVuln CurrentRawVuln
 * @param fields FieldEntries
 * @param fieldName string
 * @returns matching field entry
 */
const getMatchingField = (rawVuln: CurrentRawVuln, fields: FieldEntry[], fieldName: string) => {
  if (fieldName === 'status') {
    return _.head(fields);
  }

  return fields.find((f) => f.id === rawVuln.rawVulnAuditId);
};

/**
 * Sets status field uniformly to be a signal status across all rawvulns based on status priority.
 * Status priority is: priority -> review -> new -> anything else
 * @param status FieldEntries
 * @returns FieldEntires
 */
export const setStatusField = (status: FieldEntry[]): FieldEntry[] => {
  let statusValue = status.find(s => s.value === RawVulnStatus.priority);
  if (statusValue) {
    return [statusValue];
  }

  statusValue = status?.find(s => s.value === RawVulnStatus.review);
  if (statusValue) {
    return [statusValue];
  }

  statusValue = status?.find(s => s.value === RawVulnStatus.new);
  if (statusValue) {
    return [statusValue];
  }

  return status;
};

// Flatten and find single true value, or return falsy
export const modalHasErrors = (errorsArr: ModalErrorArray): boolean => _.find(
  _.flattenDeep(_.values(errorsArr)),
  (val) => !val,
) === false;
